import { Box, Typography, Collapse, Divider } from "@mui/material";
import React, { useEffect, useState } from "react";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SensorsIcon from "@mui/icons-material/Sensors";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleSideBar } from "../../store/slices/ui-slice";
import sidebarIcons from "./sidebarIcons";
import { mono } from "../../Theme/fonts";

const NAV_ORDER = [
  "dashboard",
  "live",
  "incident",
  "organization",
  "data",
  "report",
  "statistic",
  "person",
  "notification",
  "audit",
  "system",
];

const getSortIndex = (item) => {
  const haystack = `${(item.link || "").toLowerCase()} ${(item.title || "").toLowerCase()}`;
  const idx = NAV_ORDER.findIndex((key) => haystack.includes(key));
  return idx === -1 ? NAV_ORDER.length : idx;
};

const isLiveIntelItem = (item) =>
  (item.link || "").toLowerCase().includes("live") ||
  (item.title || "").toLowerCase().includes("live");

const Sidebar = () => {
  const rawSidebar = useSelector((state) => state.auth.filteredSidebar);
  const sortedSidebar = [...(rawSidebar || [])].sort(
    (a, b) => getSortIndex(a) - getSortIndex(b)
  );
  const hasLiveIntelInSidebar = sortedSidebar.some(isLiveIntelItem);

  const [openSections, setOpenSections] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();

  // Auto-expand any parent whose child is currently active
  useEffect(() => {
    const expanded = {};
    sortedSidebar.forEach((item) => {
      if (item?.Child_links?.some((child) => currentPath.includes(child.link))) {
        expanded[item.title] = true;
      }
    });
    setOpenSections(expanded);
  }, [currentPath]);

  const goTo = (link) => {
    dispatch(toggleSideBar());
    navigate(link);
  };

  const NavRow = ({ icon, label, active, onClick, children, isOpen }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: "14px",
        py: "9px",
        mx: "8px",
        borderRadius: "6px",
        cursor: "pointer",
        borderLeft: active ? "3px solid #0085E5" : "3px solid transparent",
        backgroundColor: active ? "#EFF6FF" : "transparent",
        "&:hover": { backgroundColor: active ? "#E8F2FF" : "#F8FAFC" },
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
    >
      {icon}
      <Typography
        sx={{
          flex: 1,
          fontFamily: mono,
          fontSize: "12px",
          fontWeight: active ? 700 : 500,
          color: active ? "#0085E5" : "#334155",
          letterSpacing: "0.3px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      {children && (
        isOpen
          ? <ExpandLess sx={{ fontSize: 15, color: active ? "#0085E5" : "#94A3B8" }} />
          : <ExpandMore sx={{ fontSize: 15, color: active ? "#0085E5" : "#94A3B8" }} />
      )}
    </Box>
  );

  const ChildRow = ({ child }) => {
    const childActive = currentPath.includes(child.link);
    return (
      <Box
        onClick={() => goTo(child.link)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          px: "14px",
          py: "8px",
          ml: "32px",
          mr: "8px",
          borderRadius: "6px",
          cursor: "pointer",
          backgroundColor: childActive ? "#EFF6FF" : "transparent",
          "&:hover": { backgroundColor: childActive ? "#E8F2FF" : "#F8FAFC" },
          transition: "background 0.15s ease",
        }}
      >
        <Box
          sx={{
            width: "5px",
            height: "5px",
            borderRadius: "50%",
            backgroundColor: childActive ? "#0085E5" : "#CBD5E1",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontFamily: mono,
            fontSize: "11px",
            fontWeight: childActive ? 700 : 400,
            color: childActive ? "#0085E5" : "#475569",
            whiteSpace: "nowrap",
          }}
        >
          {child.title}
        </Typography>
      </Box>
    );
  };

  const renderItem = (item, i) => {
    const hasChildren = item?.Child_links?.length > 0;
    const active = hasChildren
      ? item.Child_links.some((c) => currentPath.includes(c.link))
      : !!(item?.link && currentPath.includes(item.link));
    const isOpen = !!openSections[item.title];

    const icon = item.dark_icon ? (
      <Box
        component="img"
        src={sidebarIcons[item.dark_icon]}
        sx={{
          width: "16px",
          height: "16px",
          flexShrink: 0,
          // CSS filter to tint the dark icon blue when active
          filter: active
            ? "invert(31%) sepia(97%) saturate(1028%) hue-rotate(192deg) brightness(102%) contrast(101%)"
            : "opacity(0.55)",
        }}
      />
    ) : null;

    return (
      <React.Fragment key={i}>
        <NavRow
          icon={icon}
          label={item.title}
          active={active}
          isOpen={isOpen}
          onClick={() => {
            if (hasChildren) {
              setOpenSections((prev) => ({ ...prev, [item.title]: !prev[item.title] }));
            } else {
              goTo(item.link);
            }
          }}
        >
          {hasChildren || undefined}
        </NavRow>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {item.Child_links?.filter((c) => c?.link !== "/network-graph")?.map((child, ci) => (
              <ChildRow key={ci} child={child} />
            ))}
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Split: dashboard items first, then Live Intel (hardcoded if not in sidebar), then rest
  const isDashboard = (item) =>
    (item.link || "").toLowerCase().includes("dashboard") ||
    (item.title || "").toLowerCase().includes("dashboard");
  const dashItems = sortedSidebar.filter(isDashboard);
  const otherItems = sortedSidebar.filter((item) => !isDashboard(item));

  const liveActive = currentPath.includes("/live-intelligence");

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        py: "10px",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#E2E8F0", borderRadius: "4px" },
      }}
    >
      {/* Dashboard item(s) */}
      {dashItems.map((item, i) => renderItem(item, `dash-${i}`))}

      {/* Live Intelligence — hardcoded to always appear in correct position */}
      {!hasLiveIntelInSidebar && (
        <NavRow
          icon={
            <SensorsIcon
              sx={{ fontSize: 16, color: liveActive ? "#0085E5" : "#64748B", flexShrink: 0 }}
            />
          }
          label="Live Intelligence"
          active={liveActive}
          onClick={() => goTo("/live-intelligence")}
        />
      )}

      {/* Divider after top items */}
      {(dashItems.length > 0 || !hasLiveIntelInSidebar) && (
        <Divider sx={{ my: "8px", mx: "16px", borderColor: "#F1F5F9" }} />
      )}

      {/* All remaining nav items */}
      {otherItems.map((item, i) => renderItem(item, i))}
    </Box>
  );
};

export default Sidebar;
